import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ArtifactVersionRead } from '../schemas'

export type ArtifactVersionTurnIdMap = Partial<{ [artifactVersionId: string]: string }>

export type ArtifactsState = {
  artifactsVersions: Partial<{ [artifactVersionId: string]: ArtifactVersionRead }>
  // Maps artifact version id to turn id - enabling artifact-dependents to access turn-content (e.g. references).
  // Sparse — populated by ingest paths that know the link (WS ARTIFACT_VERSION_UPDATED, REST conversation load).
  // Kept as a separate map (not as a turn_id field on ArtifactVersionRead) so artifact versions stay
  // decoupled from agent turns — non-agent ingest paths can populate versions without an associated turn.
  turnIdByArtifactVersionId: ArtifactVersionTurnIdMap
}

const initialState: ArtifactsState = {
  artifactsVersions: {},
  turnIdByArtifactVersionId: {},
}

/**
 * Artifacts Slice
 * Holds artifact versions to be displayed to the user.
 */
export const artifactsSlice = createSlice({
  name: 'artifactsState',
  initialState,
  reducers: {
    // ============== Listener Actions ============== >
    startArtifactVersionStatusPoll: (_state, _action: PayloadAction<{ artifactVersionId: string }>) => {
      // Handled by the listener middleware
    },

    // ============== Slice Actions ============== >
    nullifyData: () => initialState,

    // Upsert an artifact version
    upsertArtifactVersion: (state, action: PayloadAction<ArtifactVersionRead>) => {
      state.artifactsVersions[action.payload.id] = action.payload
    },

    // Upsert a list of artifact versions
    upsertArtifactVersions: (state, action: PayloadAction<ArtifactVersionRead[]>) => {
      const artifactVersionRecords = action.payload.reduce((acc, artifactVersion) => {
        acc[artifactVersion.id] = artifactVersion
        return acc
      }, {} as Record<string, ArtifactVersionRead>)

      state.artifactsVersions = {
        ...state.artifactsVersions,
        ...artifactVersionRecords,
      }
    },

    setArtifactVersionTurnId: (state, action: PayloadAction<{ artifactVersionId: string; turn_id: string }>) => {
      state.turnIdByArtifactVersionId[action.payload.artifactVersionId] = action.payload.turn_id
    },

    setArtifactVersionTurnIds: (state, action: PayloadAction<ArtifactVersionTurnIdMap>) => {
      Object.assign(state.turnIdByArtifactVersionId, action.payload)
    },
  },
})

// Actions
export const ArtifactsActions = artifactsSlice.actions

export default artifactsSlice.reducer
